<template>
    <fullscreen-drawer :visible="visible" height="auto" title="Upload Content" :closable="false" container="body">
        <template #header-button>
            <i class="fal fa-lg fa-chevron-left" @click="handleClose" />
        </template>

        <g-loader v-if="isLoading" />
        <div v-else>
            <div class="margin-bottom-normal">
                <g-typography tag="p" value="Deliver your content and caption for brand approval." />
            </div>

            <g-form class="margin-bottom-normal">
                <g-loader v-if="isFileDeleting || isFileUploading" type="absolute" />
                <g-form-item label="Content" :errors="errors.content" required>
                    <div v-for="(file, i) in files.all" :key="i" class="margin-bottom-normal">
                        <activation-file :file="file" :deletable="true" @file-deleted="handleFileDeleted" />
                    </div>

                    <div class="margin-bottom-normal">
                        <g-upload
                            :headers="uploadHeaders"
                            :action="uploadContentUrl"
                            :multiple="true"
                            :show-upload-list="false"
                            :before-upload="handleBeforeUpload"
                            @change="handleFileChange"
                        >
                            <g-button
                                class="full-width-mobile"
                                shape="round"
                                theme="live"
                                size="large"
                                :disabled="isLoading || isSubmitting || isFileUploading || isFileDeleting"
                                :loading="isFileUploading"
                            >
                                <g-icon v-if="!isFileUploading" name="upload" />
                                Upload File
                            </g-button>
                        </g-upload>
                    </div>

                    <div class="margin-bottom-small">
                        <g-typography tag="p" size="small" color="grey-6">
                            Files accepted: .doc, .docx, .csv, .xls, .xlsx, .jpeg, .jpg, .png, .pdf, .gif, .mp4, .avi,
                            .mp3, .wav, .mov, .wmv, .flv, .webm. Up to 500mb per file.
                        </g-typography>
                    </div>
                </g-form-item>

                <g-form-item v-if="requiresCaptionApproval" label="Caption" :errors="errors.caption" required>
                    <g-text-area
                        v-model="caption"
                        placeholder="Caption, description, hashtags, mentions, etc"
                        :auto-size="{ minRows: 6, maxRows: 6 }"
                        :disabled="isLoading || isSubmitting"
                    />
                </g-form-item>
            </g-form>

            <g-button
                class="full-width-mobile"
                shape="round"
                theme="live"
                size="large"
                type="primary"
                :disabled="!isReady || isSubmitting || isFileUploading || isFileDeleting"
                :loading="isSubmitting"
                @click="handleSubmit"
            >
                Submit for Approval
            </g-button>
        </div>
    </fullscreen-drawer>
</template>

<script>
    import get from 'lodash/get';
    import { message } from '@grininc/grin-ui';
    import { ref, reactive, toRefs, computed, watch } from 'vue-demi';
    import FullscreenDrawer from '../../../../library/FullscreenDrawer.vue';
    import { CONTENT_APPROVAL_FILE_TYPE } from '../../../../../constants/activation-files';
    import { getMediaLibraryFilePaths } from '../../../../../helpers/activations/activation-files';
    import { CONTENT_TYPE_FILE, CONTENT_APPROVAL_STATUS_REJECTED } from '../../../../../constants/content-approval';
    import ActivationFile from '../../../../library/ActivationFile.vue';
    import useDeliverables from '../../../../../../src/composables/use-deliverables';
    import useHttp from '../../../../../../src/composables/use-http';

    export default {
        name: 'ActivationDeliverableContentApprovalUploadDrawer',

        components: {
            FullscreenDrawer,
            ActivationFile
        },

        props: {
            brief: {
                type: Object,
                default: null
            },
            visible: {
                type: Boolean,
                default: false
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            deliverable: {
                type: Object,
                default: null
            },
            uploadContentUrl: {
                type: String,
                default: ''
            },
            requiresCaptionApproval: {
                type: Boolean,
                default: false
            },
            uploadHeaders: {
                type: Object,
                default: null
            },
            hasUploadedFiles: {
                type: Boolean,
                default: false
            },
            uploadedFiles: {
                type: Array,
                default: null
            },
            isFileUploading: {
                type: Boolean,
                default: false
            }
        },

        setup(props, context) {
            const { deliverable, uploadedFiles, requiresCaptionApproval, isLoading } = toRefs(props);
            const isSubmitting = ref(false);
            const isFileDeleting = ref(false);
            const errors = ref({});
            const caption = ref('');

            const initialFileState = {
                new: [],
                current: [],
                all: [],
                deleted: []
            };

            const files = reactive({ ...initialFileState });

            const resetForm = () => {
                caption.value = '';
                Object.assign(files, initialFileState);
            };

            const currentActivity = computed(() => {
                return get(deliverable.value, 'last_approval_content_activity', []);
            });

            const currentHasContent = computed(() => {
                return !!currentActivity.value?.content;
            });

            const currentCaption = computed(() => {
                return currentActivity.value?.content?.description;
            });

            const currentFiles = computed(() => {
                return currentActivity.value?.content?.files;
            });

            const filterDeletedFiles = (filesToFilter) => {
                return filesToFilter?.filter((file) => !files.deleted.includes(file.name)) || [];
            };

            const getNewFiles = () => {
                const newFiles = filterDeletedFiles(uploadedFiles.value);
                return getMediaLibraryFilePaths(newFiles, CONTENT_APPROVAL_FILE_TYPE);
            };

            const getCurrentFiles = () => {
                const validCurrentFiles = filterDeletedFiles(currentFiles.value);
                return getMediaLibraryFilePaths(validCurrentFiles, CONTENT_APPROVAL_FILE_TYPE);
            };

            const isCurrentFile = (fileName) => {
                return !!files?.current?.find((file) => file.name === fileName);
            };

            const updateFileList = () => {
                files.current = getCurrentFiles();
                files.new = getNewFiles();
                files.all = [...files.current, ...files.new];
            };

            watch(isLoading, () => {
                if (isLoading.value === false) {
                    caption.value = currentCaption.value;
                    updateFileList();
                }
            });

            const isReady = computed(() => {
                const newFileAdded = files?.new?.length > 0;
                const hasFiles = files?.all?.length > 0;
                const newValidCaption = caption.value && caption.value !== currentCaption.value;
                const newContentAdded =
                    (!requiresCaptionApproval.value && hasFiles && newFileAdded) ||
                    (requiresCaptionApproval.value && hasFiles && (newFileAdded || newValidCaption));

                if (requiresCaptionApproval.value) {
                    return (
                        (currentHasContent.value && newContentAdded) ||
                        (!currentHasContent.value && caption.value && hasFiles)
                    );
                }

                return (currentHasContent.value && newContentAdded) || (!currentHasContent.value && hasFiles);
            });

            const handleBeforeUpload = (file) => {
                const isLt500MB = file.size / 1000000 < 500;

                if (!isLt500MB) {
                    message.error('File must smaller than 500MB.');
                }

                return isLt500MB;
            };

            const handleFileChange = (info) => {
                context.emit('file-change', info);
                updateFileList();
            };

            const getPayload = () => {
                const payload = {
                    files: files?.all,
                    content_type: CONTENT_TYPE_FILE
                };

                if (requiresCaptionApproval.value) {
                    payload.caption = caption.value;
                }

                /* Do Not remove
                   Adds the status of whether it was a revision or not to the payload for backend tracking
                */
                if (deliverable.approval_status === CONTENT_APPROVAL_STATUS_REJECTED) {
                    payload.revision = true;
                }

                return payload;
            };

            const handleSubmit = async () => {
                if (!isReady.value) {
                    return;
                }

                isSubmitting.value = true;

                try {
                    await useDeliverables().postApprovalContent(
                        deliverable.value?.brief_id,
                        deliverable.value?.id,
                        getPayload()
                    );

                    message.success('Your content has been submitted for approval.');

                    resetForm();
                    context.emit('refresh-deliverable');
                    context.emit('reset-uploads');
                    handleClose();
                } catch (e) {
                    handleErrorResponse(e);
                }

                isSubmitting.value = false;
            };

            const handleFileDeleted = async (fileName) => {
                const { http } = useHttp();

                try {
                    if (!isCurrentFile(fileName)) {
                        isFileDeleting.value = true;
                        const url = `/activation-briefs/${deliverable.value?.brief_id}/tasks/${deliverable.value?.task_id}/content-approval/${fileName}/delete`;
                        await http.delete(url);
                    }

                    message.success('Your file has been deleted.');
                    files.deleted.push(fileName);
                    updateFileList();
                } catch (e) {
                    message.error('There was an error while trying to delete your file, please try again later.');
                }

                isFileDeleting.value = false;
            };

            const handleErrorResponse = (response) => {
                const defaultMessage = 'There was an error while trying to submit your content, please try again later.';

                if (response.status === 423 || response.status === 409) {
                    message.error('Content has already been delivered.');
                } else if (response.status === 422) {
                    message.error(response?.body?.message || defaultMessage);
                } else {
                    message.error(defaultMessage);
                }
            };

            const handleClose = () => {
                context.emit('close');
            };

            return {
                isSubmitting,
                isFileDeleting,
                isReady,
                errors,
                caption,
                files,
                handleFileDeleted,
                handleFileChange,
                handleBeforeUpload,
                handleSubmit,
                handleClose
            };
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    ::v-deep .FullscreenDrawer__content {
        max-width: $g-screen-size-xs !important;
    }

    ::v-deep .ant-upload.ant-upload-select {
        display: block;
        margin-bottom: $g-spacing-5;
    }

    ::v-deep .ant-form-item {
        padding-bottom: $g-spacing-5;
        margin-bottom: 0;
    }

    @media (max-width: $g-screen-size-xs) {
        .full-width-mobile {
            width: 100%;
        }
    }
</style>
